// Status color tokens. Each status concept (open claim, cancelled policy,
// etc.) maps to a single user-overridable background color; the foreground
// text color is auto-derived from background luminance so the picker only
// needs one swatch per status.
//
// CSS variables are written by applyAppearance() under :root; <StatusBadge>
// reads them via inline style="background-color: var(--status-...-bg)".

import { isOpenStatus } from "@/lib/claim-status";

export type StatusKey =
  | "claim-open"
  | "claim-reopen"
  | "claim-closed"
  | "policy-active"
  | "policy-cancelled"
  | "policy-non-renewed"
  | "policy-expired"
  | "restricted"
  | "announcement-info"
  | "announcement-warning"
  | "announcement-critical";

export const STATUS_KEYS: StatusKey[] = [
  "claim-open",
  "claim-reopen",
  "claim-closed",
  "policy-active",
  "policy-cancelled",
  "policy-non-renewed",
  "policy-expired",
  "restricted",
  "announcement-info",
  "announcement-warning",
  "announcement-critical",
];

export type StatusGroup = "policy" | "claim" | "other" | "announcement";

export const STATUS_GROUP_ORDER: StatusGroup[] = ["policy", "claim", "other", "announcement"];

export const STATUS_GROUP_LABELS: Record<StatusGroup, string> = {
  policy: "Policy",
  claim: "Claim",
  other: "Other",
  announcement: "Announcement",
};

export const STATUS_DEFINITIONS: Record<
  StatusKey,
  { label: string; shortLabel: string; group: StatusGroup; defaultBg: string }
> = {
  "claim-open": { label: "Claim — Open", shortLabel: "Open", group: "claim", defaultBg: "#fde68a" },
  "claim-reopen": {
    label: "Claim — Reopen",
    shortLabel: "Reopen",
    group: "claim",
    defaultBg: "#fdba74",
  },
  "claim-closed": {
    label: "Claim — Closed",
    shortLabel: "Closed",
    group: "claim",
    defaultBg: "#e2e8f0",
  },
  "policy-active": {
    label: "Policy — Active",
    shortLabel: "Active",
    group: "policy",
    defaultBg: "#bbf7d0",
  },
  "policy-cancelled": {
    label: "Policy — Cancelled",
    shortLabel: "Cancelled",
    group: "policy",
    defaultBg: "#fecaca",
  },
  "policy-non-renewed": {
    label: "Policy — Non-Renewed",
    shortLabel: "Non-Renewed",
    group: "policy",
    defaultBg: "#fef08a",
  },
  "policy-expired": {
    label: "Policy — Expired",
    shortLabel: "Expired",
    group: "policy",
    defaultBg: "#e2e8f0",
  },
  restricted: {
    label: "Restricted",
    shortLabel: "Restricted",
    group: "other",
    defaultBg: "#cbd5e1",
  },
  "announcement-info": {
    label: "Announcement — Info",
    shortLabel: "Info",
    group: "announcement",
    // Matches sky-50 — the previous Tailwind-hardcoded info banner background.
    defaultBg: "#f0f9ff",
  },
  "announcement-warning": {
    label: "Announcement — Warning",
    shortLabel: "Warning",
    group: "announcement",
    // Matches amber-50.
    defaultBg: "#fffbeb",
  },
  "announcement-critical": {
    label: "Announcement — Critical",
    shortLabel: "Critical",
    group: "announcement",
    // Matches red-50.
    defaultBg: "#fef2f2",
  },
};

export function bgVar(key: StatusKey): string {
  return `--status-${key}-bg`;
}

export function fgVar(key: StatusKey): string {
  return `--status-${key}-fg`;
}

// Resolve a claim status string ("Open" / "Reopen" / "Closed" / etc.) to one
// of the three claim StatusKeys. Reuses isOpenStatus() — both helpers share
// the same "anything containing OPEN counts as open" rule the legacy T-SQL
// uses, so they should never drift.
export function claimStatusKey(status: string | null | undefined): StatusKey {
  if (!status) return "claim-closed";
  if (/reopen/i.test(status)) return "claim-reopen";
  if (isOpenStatus(status)) return "claim-open";
  return "claim-closed";
}

// Resolve a policy status string to one of the four policy StatusKeys.
// Normalizes whitespace/punctuation/case the same way policy-status.ts did
// before — "Non Renewed", "non-renewed", "NONRENEWED" all collapse.
export function policyStatusKey(status: string | null | undefined): StatusKey {
  const normalized = (status ?? "").toLowerCase().replace(/[\s\-_]+/g, "");
  switch (normalized) {
    case "active":
    case "reinstated":
      return "policy-active";
    case "cancelled":
    case "canceled":
      return "policy-cancelled";
    case "nonrenewed":
      return "policy-non-renewed";
    case "expired":
      return "policy-expired";
    default:
      return "policy-expired";
  }
}

// Auto-derive a readable foreground hex from any background hex. Uses the
// WCAG relative-luminance formula; returns a dark slate for light bgs and a
// near-white for dark bgs. We darken the bg's hue rather than picking pure
// black/white so the badge keeps a tinted feel.
export function deriveForeground(bgHex: string): string {
  const rgb = parseHex(bgHex);
  if (!rgb) return "#0f172a";
  const luminance = relativeLuminance(rgb);
  // Threshold tuned so all eight default backgrounds resolve to the dark fg.
  return luminance > 0.55 ? "#1e293b" : "#f8fafc";
}

function parseHex(hex: string): [number, number, number] | null {
  const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = Number.parseInt(m[1] ?? "", 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
